/**
 * ¿La petición falló por red, sin llegar a obtener respuesta?
 *
 * `fetch` rechaza con un `TypeError` cuando no hay respuesta: sin conexión,
 * DNS que no resuelve, servidor que no acepta, petición bloqueada… El
 * **mensaje**, en cambio, lo escribe cada navegador a su manera:
 *
 * - Chrome y Firefox: `Failed to fetch`
 * - Safari y todo iOS:  `Load failed`
 *
 * Comprobarlo mirando si el texto contiene «fetch» funcionaba en Chrome y
 * dejaba escapar el error en iPhone, donde un corte de red se convertía en un
 * error a pantalla completa. El tipo sí es el mismo en todos los navegadores,
 * así que es lo que se mira.
 *
 * Contrapartida asumida: un `TypeError` de programación dentro del mismo
 * `try` se confundiría con un fallo de red. Se acota manteniendo cortos los
 * bloques que envuelven a `fetch`.
 */
export function esFalloDeRed(error: unknown): boolean {
  return error instanceof TypeError;
}

/**
 * ¿El cuerpo de la respuesta no era JSON válido?
 *
 * Pasa cuando el servidor devuelve vacío o una página de error. Aquí el
 * mensaje también varía entre navegadores, así que se mira el tipo.
 */
export function esJsonInvalido(error: unknown): boolean {
  return error instanceof SyntaxError;
}

/**
 * Qué contarle a quien está mirando la pantalla.
 *
 * El mensaje de la excepción no sirve para eso: `Load failed` o
 * `Failed to fetch` no le dicen nada a nadie, y aun así se estaban pintando
 * tal cual. Un corte de red tiene su explicación; para lo demás, el texto que
 * ponga quien llama, que es el que sabe qué se estaba intentando hacer.
 *
 * El detalle técnico no se pierde: sigue yendo a la consola donde se captura.
 */
export function mensajeDeFallo(error: unknown, porDefecto: string): string {
  if (esFalloDeRed(error)) {
    return 'No se puede conectar con el servidor. Revisa tu conexión.';
  }
  return porDefecto;
}
